import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { updateApplicationForm } from "../../../utils/Api";
import { toast } from "react-toastify";

const Step2 = ({ setActiveStep, oldFormData, updateFormData }) => {
  // NEW: Check if course is LLB or LLM
  const isLLBOrLLM =
    oldFormData?.course?.toLowerCase() === "llb" ||
    oldFormData?.course?.toLowerCase() === "llm" ||
    oldFormData?.course?.toLowerCase() === "ba llb";

  // NEW: Check if student is admitted
  //const isAdmitted = oldFormData?.formStatusFromAdmin === "Student Admitted";

  // NEW: Show degree section only for LLB/LLM admitted students
  const showGraduationDegree = isLLBOrLLM;

  const [tenthPercentage, setTenthPercentage] = useState(0);
  const [twelfthPercentage, setTwelfthPercentage] = useState(0);

  const {
    register,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: oldFormData,
  });
  const [graduationDuration, setGraduationDuration] = useState(
    oldFormData?.academicQualifications?.graduationDuration || 0,
  );

  useEffect(() => {
    if (oldFormData && oldFormData?.applicationNumber) {
      reset(oldFormData);
      if (oldFormData.academicQualifications?.graduationDuration) {
        setGraduationDuration(
          oldFormData.academicQualifications.graduationDuration,
        );
      }
    }
  }, [reset, oldFormData]);

  useFieldArray({
    control,
    name: "competitiveExamScore",
  });

  const {
    fields: graduationFields,
    append: appendGraduation,
    remove: removeGraduation,
  } = useFieldArray({
    control,
    name: "academicQualifications.graduation",
  });

  useEffect(() => {
    const currentGraduationFields =
      watch("academicQualifications.graduation")?.length || 0;
    if (currentGraduationFields < graduationDuration) {
      for (let i = currentGraduationFields; i < graduationDuration; i++) {
        appendGraduation({
          year: `Year ${i + 1}`,
          percentage: "",
        });
      }
    } else if (currentGraduationFields > graduationDuration) {
      for (let i = currentGraduationFields; i > graduationDuration; i--) {
        removeGraduation(i - 1);
      }
    }
  }, [graduationDuration, appendGraduation, removeGraduation, watch]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Handle 10th percentage
      if (
        name === "academicQualifications.tenth.obtainedMarks" ||
        name === "academicQualifications.tenth.maximumMarks"
      ) {
        const obtained = value.academicQualifications?.tenth?.obtainedMarks;
        const max = value.academicQualifications?.tenth?.maximumMarks;
        if (obtained && max) {
          const percentage = (obtained / max) * 100;
          setTenthPercentage(percentage.toFixed(2));
          setValue(
            "academicQualifications.tenth.percentage",
            percentage.toFixed(2),
          );
        } else {
          setTenthPercentage(0);
          setValue("academicQualifications.tenth.percentage", "");
        }
      }

      // Handle 12th percentage
      if (
        name === "academicQualifications.twelfth.obtainedMarks" ||
        name === "academicQualifications.twelfth.maximumMarks"
      ) {
        const obtained = value.academicQualifications?.twelfth?.obtainedMarks;
        const max = value.academicQualifications?.twelfth?.maximumMarks;
        if (obtained && max) {
          const percentage = (obtained / max) * 100;
          setTwelfthPercentage(percentage.toFixed(2));
          setValue(
            "academicQualifications.twelfth.percentage",
            percentage.toFixed(2),
          );
        } else {
          setTwelfthPercentage(0);
          setValue("academicQualifications.twelfth.percentage", "");
        }
      }

      // Handle graduation percentages
      if (name && name.startsWith("academicQualifications.graduation")) {
        const parts = name.split(".");
        if (
          parts.length === 4 &&
          (parts[3] === "obtainedMarks" || parts[3] === "maximumMarks")
        ) {
          const index = parseInt(parts[2], 10);
          if (!isNaN(index)) {
            const gradYear = value.academicQualifications.graduation[index];
            if (gradYear && gradYear.obtainedMarks && gradYear.maximumMarks) {
              const percentage =
                (gradYear.obtainedMarks / gradYear.maximumMarks) * 100;
              setValue(
                `academicQualifications.graduation.${index}.percentage`,
                percentage.toFixed(2),
              );
            } else {
              setValue(
                `academicQualifications.graduation.${index}.percentage`,
                "",
              );
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const onSubmit = async (data) => {
    const formData = new FormData();
    const aq = data.academicQualifications;

    if (aq) {
      // 10th details
      if (aq.tenth) {
        formData.append(
          "academicQualifications[tenth][institution]",
          aq.tenth.institution,
        );
        formData.append(
          "academicQualifications[tenth][percentage]",
          aq.tenth.percentage,
        );
        formData.append(
          "academicQualifications[tenth][yearOfPassing]",
          aq.tenth.yearOfPassing,
        );
      }

      // 12th details
      if (aq.twelfth) {
        formData.append(
          "academicQualifications[twelfth][institution]",
          aq.twelfth.institution,
        );
        formData.append(
          "academicQualifications[twelfth][percentage]",
          aq.twelfth.percentage,
        );
        formData.append(
          "academicQualifications[twelfth][yearOfPassing]",
          aq.twelfth.yearOfPassing,
        );
      }

      // CET Exam details
      if (aq.cetExam) {
        formData.append(
          "academicQualifications[cetExam][examYear]",
          aq.cetExam.examYear,
        );
        formData.append(
          "academicQualifications[cetExam][percentile]",
          aq.cetExam.percentile,
        );
      }

      // Graduation details
      if (aq && aq.graduationDuration) {
        formData.append(
          "academicQualifications[graduationDuration]",
          aq.graduationDuration || 0,
        );
        if (aq.graduationInstitution) {
          formData.append(
            "academicQualifications[graduationInstitution]",
            aq.graduationInstitution,
          );
        }
        if (aq.graduationDegree) {
          formData.append(
            "academicQualifications[graduationDegree]",
            aq.graduationDegree,
          );
        }
        if (aq.graduationYearOfPassing) {
          formData.append(
            "academicQualifications[graduationYearOfPassing]",
            aq.graduationYearOfPassing,
          );
        }
      }

      if (aq.graduation) {
        aq.graduation.forEach((grad, index) => {
          formData.append(
            `academicQualifications[graduation][${index}][degree]`,
            aq.graduationDegree || "",
          );
          formData.append(
            `academicQualifications[graduation][${index}][cgpa]`,
            grad.cgpa,
          );
          formData.append(
            `academicQualifications[graduation][${index}][year]`,
            grad.year,
          );
        });
      }
    }

    if (!(data.stage > 2)) {
      formData.append("stage", 2);
    }

    const formId = oldFormData?._id;
    if (!formId) {
      console.error("No _id found in oldFormData:", oldFormData);
      toast.error("Application ID not found");
      return;
    }

    formData.append("_id", formId);

    try {
      await updateApplicationForm(formData);

      updateFormData({
        ...oldFormData,
        academicQualifications: {
          ...oldFormData.academicQualifications,
          ...data.academicQualifications,
        },
      });

      setActiveStep((prevStep) => prevStep + 1);
    } catch (error) {
      console.log(error);
      toast.error(`Try Again, ${error?.response?.data?.error}`);
    }
  };

  const formInputsAcademic10 = [
    {
      label: "10th Institution",
      type: "text",
      name: "academicQualifications.tenth.institution",
      required: true,
      placeholder: "ABC College, XYZ School...",
    },
    {
      label: "10th Obtained Marks",
      type: "number",
      name: "academicQualifications.tenth.obtainedMarks",
      required: true,
      placeholder: "Enter marks obtained",
    },
    {
      label: "10th Maximum Marks",
      type: "number",
      name: "academicQualifications.tenth.maximumMarks",
      required: true,
      placeholder: "Enter maximum marks",
    },
    {
      label: "10th Percentage",
      type: "text",
      name: "academicQualifications.tenth.percentage",
      required: true,
      readOnly: true,
      value: tenthPercentage,
    },
    {
      label: "10th Year of Passing",
      type: "year",
      name: "academicQualifications.tenth.yearOfPassing",
      required: true,
      placeholder: "2021,2020,2022...",
    },
  ];

  const formInputsAcademic12 = [
    {
      label: "12th Institution",
      type: "text",
      name: "academicQualifications.twelfth.institution",
      required: true,
      placeholder: "ABC College, XYZ School...",
    },
    {
      label: "12th Obtained Marks",
      type: "number",
      name: "academicQualifications.twelfth.obtainedMarks",
      required: true,
      placeholder: "Enter marks obtained",
    },
    {
      label: "12th Maximum Marks",
      type: "number",
      name: "academicQualifications.twelfth.maximumMarks",
      required: true,
      placeholder: "Enter maximum marks",
    },
    {
      label: "12th Percentage",
      type: "text",
      name: "academicQualifications.twelfth.percentage",
      required: true,
      readOnly: true,
      value: twelfthPercentage,
    },
    {
      label: "12th Year of Passing",
      type: "year",
      name: "academicQualifications.twelfth.yearOfPassing",
      required: true,
      placeholder: "2021,2020,2022...",
    },
  ];

  const cetExamInputs = [
    {
      label: "CET Exam Year",
      type: "text",
      name: "academicQualifications.cetExam.examYear",
      required: true,
      placeholder: "2023",
    },
    {
      label: "CET Percentile",
      type: "text",
      name: "academicQualifications.cetExam.percentile",
      required: true,
      placeholder: "Enter percentile",
    },
  ];

  const graduationInputs = [
    {
      name: "academicQualifications.graduationDuration",
      label: "How many years was your graduation?",
      type: "number",
      required: false,
      placeholder: "e.g., 4",
    },
    {
      name: "academicQualifications.graduationInstitution",
      label: "Name of Institution",
      type: "text",
      required: false,
      placeholder: "e.g., VIT University",
    },
    {
      name: "academicQualifications.graduationYearOfPassing",
      label: "Year of Passing",
      type: "number",
      required: false,
      placeholder: "e.g., 2023",
    },
    {
      name: "academicQualifications.graduationDegree",
      label: "Degree",
      type: "text",
      required: false,
      placeholder: "e.g., B.Tech, B.Com",
    },
  ];
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* 10th details */}
      <h4 className="text-light-black my-3">
        &larr; 10th Standard Details &rarr;
      </h4>
      {formInputsAcademic10.map((input, index) => (
        <div className="form-group" key={index}>
          <label>
            {input.label}{" "}
            <span className="text-danger">{input.required ? "*" : ""}</span>
          </label>
          <input
            className="form-control p_input"
            placeholder={input?.placeholder}
            type={input.type}
            readOnly={input.readOnly}
            value={input.readOnly ? input.value : undefined}
            {...register(input.name, {
              required: input.required && !input.readOnly,
            })}
            aria-invalid={
              errors?.academicQualifications?.tenth?.[
                input.name.split(".").pop()
              ]
                ? "true"
                : "false"
            }
          />
          {errors?.academicQualifications?.tenth?.[input.name.split(".").pop()]
            ?.type === "required" && (
            <p className="text-danger">{`${input.label} is required`}</p>
          )}
          {errors?.academicQualifications?.tenth?.[input.name.split(".").pop()]
            ?.type === "pattern" && (
            <p className="text-danger">
              {
                errors?.academicQualifications?.tenth?.[
                  input.name.split(".").pop()
                ]?.message
              }
            </p>
          )}
        </div>
      ))}

      {/* 12th details */}
      <h4 className="text-light-black my-3">
        &larr; 12th Standard Details &rarr;
      </h4>
      {formInputsAcademic12.map((input, index) => (
        <div className="form-group" key={index}>
          <label>
            {input.label}{" "}
            <span className="text-danger">{input.required ? "*" : ""}</span>
          </label>
          <input
            className="form-control p_input"
            placeholder={input?.placeholder}
            type={input.type}
            readOnly={input.readOnly}
            value={input.readOnly ? input.value : undefined}
            {...register(input.name, {
              required: input.required && !input.readOnly,
            })}
            aria-invalid={
              errors?.academicQualifications?.twelfth?.[
                input.name.split(".").pop()
              ]
                ? "true"
                : "false"
            }
          />
          {errors?.academicQualifications?.twelfth?.[
            input.name.split(".").pop()
          ]?.type === "required" && (
            <p className="text-danger">{`${input.label} is required`}</p>
          )}
          {errors?.academicQualifications?.twelfth?.[
            input.name.split(".").pop()
          ]?.type === "pattern" && (
            <p className="text-danger">
              {
                errors?.academicQualifications?.twelfth?.[
                  input.name.split(".").pop()
                ]?.message
              }
            </p>
          )}
        </div>
      ))}

      {/* CET Exam Details */}
      <h4 className="text-light-black my-3">&larr; CET Exam Details &rarr;</h4>
      <div className="row">
        {cetExamInputs.map((input) => (
          <div className="col-md-4" key={input.name}>
            <div className="form-group">
              <label>
                {input.label}
                <span className="text-danger">{input.required ? "*" : ""}</span>
              </label>
              <input
                className="form-control p_input"
                placeholder={input.placeholder}
                type={input.type}
                {...register(input.name, {
                  required: input.required,
                })}
                aria-invalid={
                  errors?.academicQualifications?.cetExam?.[
                    input.name.split(".").pop()
                  ]
                    ? "true"
                    : "false"
                }
              />
              {errors?.academicQualifications?.cetExam?.[
                input.name.split(".").pop()
              ]?.type === "required" && (
                <p className="text-danger">{`${input.label} is required`}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Graduation Details */}
        <h4 className="text-light-black my-3">
          &larr; Graduation Details &rarr;
        </h4>
        <div className="form-group">
          <label>
            How many years was your graduation?
            <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className="form-control p_input"
            placeholder="e.g., 3"
            {...register("academicQualifications.graduationDuration", {
              valueAsNumber: true,
              required: true,
            })}
            onChange={(e) =>
              setGraduationDuration(parseInt(e.target.value, 10) || 0)
            }
          />
          <small className="form-text text-muted">
            Enter the duration of your graduation in years. If you haven't
            completed a graduation, please enter 0.
          </small>
          {errors?.academicQualifications?.graduationDuration?.type ===
            "required" && (
            <p className="text-danger">Graduation duration is required</p>
          )}
        </div>

        {graduationDuration > 0 && (
          <>
            <div className="form-group">
              <label>
                Institution Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control p_input"
                placeholder="Institution Name"
                {...register("academicQualifications.graduationInstitution", {
                  required: true,
                })}
              />
              {errors?.academicQualifications?.graduationInstitution?.type ===
                "required" && (
                <p className="text-danger">Institution Name is required</p>
              )}
            </div>
            <div className="form-group">
              <label>
                Year of Passing <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control p_input"
                placeholder="Year of Passing"
                {...register("academicQualifications.graduationYearOfPassing", {
                  valueAsNumber: true,
                  required: true,
                })}
              />
              {errors?.academicQualifications?.graduationYearOfPassing?.type ===
                "required" && (
                <p className="text-danger">Year of Passing is required</p>
              )}
            </div>

            {/* Degree Dropdown - Single, not repeated */}
            <div className="form-group">
              <label>
                Degree <span className="text-danger">*</span>
              </label>
              <select
                className="form-control p_input"
                {...register("academicQualifications.graduationDegree", {
                  required: true,
                })}
              >
                <option value="">Select Degree</option>
                <option value="B.Tech">B.Tech</option>
                <option value="M.Tech">M.Tech</option>
                <option value="B.E">B.E</option>
                <option value="M.E">M.E</option>
                <option value="B.Sc">B.Sc</option>
                <option value="M.Sc">M.Sc</option>
                <option value="B.A">B.A</option>
                <option value="M.A">M.A</option>
                <option value="B.Com">B.Com</option>
                <option value="M.Com">M.Com</option>
                <option value="Other">Other</option>
              </select>
              {errors?.academicQualifications?.graduationDegree?.type ===
                "required" && (
                <p className="text-danger">Degree is required</p>
              )}
            </div>
          </>
        )}

        {graduationFields.map((field, index) => (
          <div key={field.id} className="border p-3 mb-3">
            <h5>Year {index + 1}</h5>
            <div className="row">
              {/* CGPA */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    CGPA <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="10"
                    className="form-control p_input"
                    placeholder="e.g., 8.5"
                    {...register(`academicQualifications.graduation.${index}.cgpa`, {
                      required: true,
                      min: 0,
                      max: 10,
                    })}
                  />
                  {errors?.academicQualifications?.graduation?.[index]?.cgpa
                    ?.type === "required" && (
                    <p className="text-danger">CGPA is required</p>
                  )}
                  {errors?.academicQualifications?.graduation?.[index]?.cgpa
                    ?.type === "min" && (
                    <p className="text-danger">CGPA must be between 0 and 10</p>
                  )}
                </div>
              </div>

              {/* Year */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>Year</label>
                  <input
                    className="form-control p_input"
                    readOnly
                    {...register(
                      `academicQualifications.graduation.${index}.year`,
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-center">
          <button
            className="btn btn-light btn-block enter-btn"
            onClick={() => setActiveStep((prevStep) => prevStep - 1)}
          >
            Previous
          </button>
        </div>
        <div className="text-center">
          <button
            type="submit"
            className="btn btn-primary btn-block enter-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Please wait..." : "Next"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step2;
